<template>
  <div class="customers-dialog modal-footer">
    <SDialog v-model:open="modalVisibility">
      <SDialogContent
        :show-close-button="false"
        class="customers-dialog customers-modal !fixed !inset-0 !top-0 !left-0 !w-screen !h-screen !max-w-none !rounded-none !translate-x-0 !translate-y-0 !p-0 !border-0 !gap-0 !grid-rows-[auto_auto_1fr] overflow-y-auto overflow-x-hidden [&>*]:min-w-0"
        data-cy="customers-modal"
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle class="sr-only">
            <template v-if="isInvoiceWithoutCustomer">
              {{ $t("customers.invoice-with-no-customer-title") }}
            </template>
            <template v-else>
              {{ $t("customers.title") }}
            </template>
          </SDialogTitle>
        </SDialogHeader>

        <div class="customized-header d-flex flex-row fl-s-b">
          <div class="header-title d-flex fl-col fl-j-c">
            <p v-if="isInvoiceWithoutCustomer">
              {{ $t("customers.invoice-with-no-customer-title") }}
            </p>
            <p v-else>{{ $t("customers.title") }}</p>
          </div>

          <div class="close-button d-flex fl-col fl-j-c" @click="close">
            <SvgIcon
              :iconContent="closeIconContent"
              :iconAlt="closeIconAlt"
              :iconStyle="closeIconStyle"
            />
          </div>
        </div>

        <div class="customized-body">
          <div class="mb-3 flex justify-end">
            <SButton
              type="button"
              size="sm"
              data-cy="customer-create-link"
              @click="toggleModalVisible(ModalStatus.CustomerCreate)"
            >
              <PlusIcon class="h-4 w-4" />
              {{ $t("customers.link") }}
            </SButton>
          </div>

          <CustomersSearchTable
            v-if="groupId"
            :key="groupId"
            :group-id="groupId"
          />
        </div>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref } from "vue";

import CustomersModule from "@/store/modules/customers";
import UserModule from "@/store/modules/user";
import ModalModule from "@/store/modules/modal";
import { useOrderMetaStore } from "@/stores/order-meta.store";

import {
  dialogVisibleForStatus,
  toggleModalVisible,
} from "@/utils/vue-helpers/modal";
import {
  ASSIGNED_CUSTOMER_REMOVABLE_KEY,
  canRemoveAssignedCustomer,
} from "@/utils/vue-helpers/customer";
import { ModalStatus } from "@/constants/modal";

import { PaymentMethod } from "@smeetz/orders";

import closeIcon from "@/assets/images/svg/close.svg";

import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";
import { PlusIcon } from "@smeetz/ui-icons";

import CustomersSearchTable from "./CustomersSearchTable.vue";

// =============================================================================
// State
// =============================================================================

const orderMetaStore = useOrderMetaStore();

const modalVisibility = dialogVisibleForStatus(ModalStatus.Customers);

const closeIconContent = closeIcon;
const closeIconAlt = "Close";
const closeIconStyle =
  "width: 30px; height: 30px; filter: contrast(170%) invert(100%);";

// APPS-689: read by each row's actions cell. Starts false so the assigned
// customer cannot be removed before the order's state is known.
const isAssignedCustomerRemovable = ref<boolean>(false);

provide(ASSIGNED_CUSTOMER_REMOVABLE_KEY, isAssignedCustomerRemovable);

// =============================================================================
// Computed
// =============================================================================

/**
 * Active group id, drives the per-group ES filter inside SSearchTable. The
 * `:key` binding remounts the wrapper on group switch.
 *
 * @returns {number | null}
 */
const groupId = computed((): number | null => {
  return UserModule.getUserGroupId;
});

/**
 * Alternate title for the QR-code-bank-transfer flow when no customer is
 * attached yet — preserves existing UX.
 *
 * @returns {boolean}
 */
const isInvoiceWithoutCustomer = computed((): boolean => {
  return (
    orderMetaStore.paymentMethod ===
      PaymentMethod.Swiss_QR_Code_Bank_Transfer &&
    !CustomersModule.getSelectedCustomerId
  );
});

// =============================================================================
// Handlers
// =============================================================================

/**
 * Close the customers modal.
 *
 * @returns {void}
 */
function close(): void {
  ModalModule.setStatus(ModalStatus.Hidden);
}

// =============================================================================
// Lifecycle
// =============================================================================

onMounted((): void => {
  isAssignedCustomerRemovable.value = canRemoveAssignedCustomer();
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

.customers-modal {
  .customized-header {
    padding-left: 30px;
    padding-right: 30px;
    background-color: #004c53;
    height: 45px;
    color: white;

    .header-title {
      p {
        font-size: 18px;
        font-weight: 700;
        margin: 0;
      }
    }

    .close-button {
      cursor: pointer;
    }
  }

  .customized-body {
    padding: 20px;
    height: calc(100% - 45px);
  }
}
</style>
